import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';

const SITE_URL = 'https://mobiluxestore.com';
const DEFAULT_IMAGE = `${SITE_URL}/images/mobiluxe_store.png`;

const ensureMeta = (selector, createAttributes) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    Object.entries(createAttributes).forEach(([key, value]) => element.setAttribute(key, value));
    document.head.appendChild(element);
  }
  return element;
};

const ensureLink = (rel) => {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  return element;
};

function SEO({
  title,
  description,
  keywords = [],
  image = DEFAULT_IMAGE,
  canonicalPath,
  robots = 'index,follow',
  structuredData = [],
}) {
  const location = useLocation();
  const { language } = useLanguage();
  const canonicalUrl = `${SITE_URL}${canonicalPath || location.pathname}`;
  const fullTitle = title.includes('Mobiluxe') ? title : `${title} | Mobiluxe Store`;

  useEffect(() => {
    document.title = fullTitle;

    ensureMeta('meta[name="description"]', { name: 'description' }).setAttribute('content', description);
    ensureMeta('meta[name="robots"]', { name: 'robots' }).setAttribute('content', robots);
    ensureMeta('meta[name="keywords"]', { name: 'keywords' }).setAttribute('content', keywords.join(', '));

    ensureMeta('meta[property="og:title"]', { property: 'og:title' }).setAttribute('content', fullTitle);
    ensureMeta('meta[property="og:description"]', { property: 'og:description' }).setAttribute('content', description);
    ensureMeta('meta[property="og:type"]', { property: 'og:type' }).setAttribute('content', 'website');
    ensureMeta('meta[property="og:url"]', { property: 'og:url' }).setAttribute('content', canonicalUrl);
    ensureMeta('meta[property="og:image"]', { property: 'og:image' }).setAttribute('content', image);
    ensureMeta('meta[property="og:locale"]', { property: 'og:locale' }).setAttribute('content', language === 'ar' ? 'ar_MA' : 'fr_MA');

    ensureMeta('meta[name="twitter:card"]', { name: 'twitter:card' }).setAttribute('content', 'summary_large_image');
    ensureMeta('meta[name="twitter:title"]', { name: 'twitter:title' }).setAttribute('content', fullTitle);
    ensureMeta('meta[name="twitter:description"]', { name: 'twitter:description' }).setAttribute('content', description);
    ensureMeta('meta[name="twitter:image"]', { name: 'twitter:image' }).setAttribute('content', image);

    ensureLink('canonical').setAttribute('href', canonicalUrl);

    const scriptId = 'mobiluxe-jsonld';
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(structuredData.length === 1 ? structuredData[0] : structuredData);
  }, [canonicalUrl, description, fullTitle, image, keywords, language, robots, structuredData]);

  return null;
}

export { SITE_URL, DEFAULT_IMAGE };
export default SEO;
